'use client';

import Link from 'next/link';

import { ArrowRight } from 'lucide-react';

import type { MarketingLocale } from '~/lib/marketing/site';
import { localizedHref } from '~/lib/marketing/routes';

import { BUILT_WITH_YOU } from '../_lib/platform.content';
import { FitScale } from './product-demos/demo-kit';
import { FEATURE_REQUEST_DEMO_SIZE } from './product-demos/demo-sizes';
import { FeatureRequestDemo } from './product-demos/feature-request-demo';

const READ_MORE = { en: 'How we work with you', ar: 'كيف نعمل معك' } as const;

/**
 * Compact version of the landing page's "Built with you" section for the
 * audience pages: the promise, the shipped-features stat and the live board.
 */
export function BuiltWithYouBand({ locale }: { locale: MarketingLocale }) {
  return (
    <section className="bg-[#202020] px-4 py-14 text-white md:px-8 md:py-20 lg:px-[130px]">
      <div className="mx-auto grid max-w-[1255px] grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-14">
        <div>
          <div className="mb-4 flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-[#a9c8ff]" aria-hidden="true" />
            <p className="font-roboto-mono text-[12px] uppercase tracking-wide text-[#a9c8ff] md:text-[14px]">
              {BUILT_WITH_YOU.label[locale]}
            </p>
          </div>
          <h2
            className="font-roboto text-[34px] leading-[1.05] tracking-tight md:text-[48px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {BUILT_WITH_YOU.heading[locale]}
          </h2>
          <p className="mt-5 font-roboto text-[16px] leading-relaxed text-white/65 md:text-[17px]">
            {BUILT_WITH_YOU.description[locale]}
          </p>
          <dl className="mt-8 flex gap-10">
            {BUILT_WITH_YOU.stats.slice(0, 2).map((stat) => (
              <div key={stat.label.en} className="flex flex-col-reverse">
                <dt className="mt-1 font-roboto text-[13px] text-white/50">{stat.label[locale]}</dt>
                <dd className="font-roboto text-[28px] font-bold md:text-[34px]">{stat.value[locale]}</dd>
              </div>
            ))}
          </dl>
          <Link
            href={localizedHref('/#built-with-you', locale)}
            className="group mt-8 inline-flex items-center gap-2 font-roboto-mono text-[12px] uppercase tracking-wide text-white/80 transition-colors hover:text-white"
          >
            {READ_MORE[locale]}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
          </Link>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-white/[0.03] p-4 md:p-6">
          <FitScale width={FEATURE_REQUEST_DEMO_SIZE.width} height={FEATURE_REQUEST_DEMO_SIZE.height}>
            <FeatureRequestDemo locale={locale} />
          </FitScale>
        </div>
      </div>
    </section>
  );
}
